import styled from "styled-components";
import { useState, useRef, useEffect, useMemo } from "react";
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import * as TypographyStyles from '../TypographyStyles';
import { StyledTooltip } from '../TooltipStyles';
import { formatDuration, getDurationInMinutes } from "../../utils/durationUtils";
import { getLineName, Leg } from "../../utils/mapJourneysData";
import { colors } from "../../utils/colors";
import AmenityIcon, { getIconNameForCode } from "./AmenityIcon";
import info from '../../icons/info.svg'
import { slateColors, purpleColors } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';

import { TimeContainer } from '../Elements/TimeComponents'
import Location from "./Location";
import LineBadge from "../Elements/LineBadge";
import Statistics from "./Statistics";
import StopoversCard from "./StopoversCard";

const Container = styled('div')<{ $bothCancelled?: boolean; $remarksHeight?: number }>`
 display: flex;
 flex-direction: column;
 padding: 1.5rem;
 align-items: flex-start;
 gap: 0.25rem;
 align-self: stretch;
 border-radius: 2.5rem;
 border: 1px solid ${slateColors[200]};
 background: white;
 box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
 padding-left: 3rem;
 position: relative;
 transition: all 0.3s;
 
 /* CSS variable for remarks height */
 --remarks-height: ${props => props.$remarksHeight || 0}px;
 
 &:hover {
   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
   border-color: ${slateColors[300]};
 }
 
 &:before {
   content: "";
   position: absolute;
   width: 2px;
   background-color: ${props => props.$bothCancelled ? colors.red : purpleColors[500]};
   z-index: 0;
   /* Center horizontally with dots: 
      Container padding-left: 3rem (48px)
      Location Name margin-left: -32px
      Dot width: 20px, so dot starts at 48px - 32px = 16px
      Dot center: 16px + 10px = 26px
      Line width: 2px, so line left: 26px - 1px = 25px */
   left: 25px;
   /* Start from center of first dot (20px tall, centered vertically with text):
      padding top (24px) + TimeContainer height (~24px) + gap (4px) + half dot height (10px) */
   top: calc(1.5rem + 24px + 0.25rem + 10px);
   /* End at center of second dot (not beyond it):
      The destination Location is always positioned the same way, but its distance from bottom changes.
      The Location component has text (~24px) with a dot (20px) centered in it.
      Without remarks: padding bottom (24px) + half Location text height (12px) to reach dot center
      With remarks: Location is further from bottom, add remarks container spacing (margin-top 12px + padding-top 12px = 24px) 
      plus the actual remarks height from CSS variable */
   bottom: ${props => props.$remarksHeight ? 'calc(1.5rem + 24px + var(--remarks-height) + 12px)' : 'calc(1.5rem + 12px)'};
 }
`

const LineContainer = styled('div')`
 margin-top: 12px;
 display: flex;
 flex-direction: column;
 grid-row-gap: 12px;
 margin-bottom: 12px;
`

const BadgeContainer = styled('div')`
 display: flex;
 align-items: center;
 grid-column-gap: 10px;
`

const AmenitiesContainer = styled('div')`
 display: flex;
 align-items: center;
 grid-column-gap: 8px;
 position: relative;
 z-index: 10;
`

interface DestinationContainerProps {
  $hasDirection?: boolean;
}

const DestinationContainer = styled('div')<DestinationContainerProps>`
 display: flex;
  gap: 10px;
`

const InfoContainer = styled('div')`
 color: ${colors.infoBlue};
 display: flex;
 grid-column-gap: 4px;
 align-items: center;
 & > img {
   height: 12px;
 }
}
`

const StatsContainer = styled('div')`
 margin-bottom: 14px;
 width: 100%;
`

const ClickableStopCount = styled(TypographyStyles.BodySRegular)<{ $clickable: boolean }>`
  ${props => props.$clickable && `
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  `}
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  box-sizing: border-box;
`;

const RemarksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${slateColors[200]};
`;

const RemarkItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const RemarkContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const RemarkIcon = styled(Info)`
  width: 14px;
  height: 14px;
  color: ${slateColors[500]};
  flex-shrink: 0;
  margin-top: 2px;
`;

const RemarkText = styled(TypographyStyles.BodySRegular)<{ $expanded?: boolean }>`
  color: ${slateColors[600]};
  flex: 1;
  line-height: 1.4;
  ${props => !props.$expanded && `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: ${purpleColors[500]};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  margin-left: 1.75rem;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: inherit;
  text-align: left;
  
  &:hover {
    color: ${purpleColors[600]};
    text-decoration: underline;
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

type JourneyLegCardProps = {
  leg: Leg;
}

const JourneyLegCard = ({ leg }: JourneyLegCardProps) => {
  const { t } = useTranslation();
  const [isStopoversOpen, setIsStopoversOpen] = useState(false);
  const [remarksHeight, setRemarksHeight] = useState(0);
  const [expandedRemarks, setExpandedRemarks] = useState<Set<number>>(new Set());
  const [needsExpansion, setNeedsExpansion] = useState<Set<number>>(new Set());
  const remarksRef = useRef<HTMLDivElement>(null);
  const remarkTextRefs = useRef<Map<number, HTMLElement>>(new Map());
  const remark = leg.remarks?.find((remark: { code?: string }) => remark.code === null)

  // Filter amenities: if both bicycle-conveyance and bicycle-conveyance-reservation are present,
  // only show bicycle-conveyance-reservation. Also deduplicate by icon (each icon shown only once).
  const getFilteredAmenities = (): Array<{ code?: string }> | null => {
    if (!leg.remarks) return null;
    
    // Filter to only remarks with codes (amenities)
    const amenities = leg.remarks.filter((remark: { code?: string }) => remark.code != null);
    
    if (amenities.length === 0) return null;
    
    // Check if bicycle-conveyance-reservation exists
    const hasReservation = amenities.some((r: { code?: string }) => r.code === 'bicycle-conveyance-reservation');
    
    // Filter: exclude bicycle-conveyance if bicycle-conveyance-reservation is present
    let filtered = amenities.filter((remark: { code?: string }) => {
      if (remark.code === 'bicycle-conveyance') {
        return !hasReservation;
      }
      return true;
    });
    
    // Deduplicate by icon name - each icon should only appear once
    // This will catch any remaining duplicates (e.g., if both bicycle codes somehow made it through)
    // Also prioritize certain codes (e.g., bicycle-conveyance-reservation over bicycle-conveyance)
    const seenIconNames = new Map<string, { code?: string }>();
    filtered.forEach((remark: { code?: string }) => {
      if (!remark.code) return;
      
      const iconName = getIconNameForCode(remark.code);
      if (!iconName) return;
      
      // If we've already seen this icon, check if we should replace it with this one
      const existing = seenIconNames.get(iconName);
      if (existing) {
        // Prioritize bicycle-conveyance-reservation over bicycle-conveyance
        if (existing.code === 'bicycle-conveyance' && remark.code === 'bicycle-conveyance-reservation') {
          seenIconNames.set(iconName, remark);
        }
        // Otherwise, keep the first one we saw
      } else {
        seenIconNames.set(iconName, remark);
      }
    });
    
    // Convert map back to array
    filtered = Array.from(seenIconNames.values());
    
    return filtered.length > 0 ? filtered : null;
  };

  const filteredAmenities = getFilteredAmenities();

  // Check if departure/arrival are cancelled
  // Cancelled if: leg.cancelled is true, or if there's a planned time but no actual time and no delay
  const isDepartureCancelled = !!(leg.cancelled === true || 
    (leg.plannedDeparture && !leg.departure && leg.departureDelay === null));
  const isArrivalCancelled = !!(leg.cancelled === true || 
    (leg.plannedArrival && !leg.arrival && leg.arrivalDelay === null));
  const bothCancelled = !!(isDepartureCancelled && isArrivalCancelled);
  
  // Check if there are remarks (excluding hints) to adjust line height
  const filteredRemarks = useMemo(() => {
    return leg.remarks?.filter((remark: any) => {
      const isNotHint = remark.type?.toLowerCase() !== 'hint';
      const hasText = remark.text && remark.text.trim().length > 0;
      return isNotHint && hasText;
    }) || [];
  }, [leg.remarks]);
  const hasRemarks = filteredRemarks.length > 0;

  // Check if remarks need expansion (more than 2 lines)
  // Use a stable key based on remark texts to avoid infinite loops
  const remarksKey = useMemo(() => {
    return filteredRemarks.map((r: any, idx: number) => `${r.text}-${idx}`).join('|');
  }, [filteredRemarks]);

  useEffect(() => {
    // Only check after DOM has been updated
    const timeoutId = setTimeout(() => {
      const newNeedsExpansion = new Set<number>();
      remarkTextRefs.current.forEach((element, index) => {
        if (element) {
          // Check if the element's scrollHeight is greater than its clientHeight
          // This indicates text is being truncated
          const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 16.8; // Default to 1.4 * 12px
          const maxHeight = lineHeight * 2; // 2 lines
          if (element.scrollHeight > maxHeight + 2) { // Add 2px tolerance
            newNeedsExpansion.add(index);
          }
        }
      });
      setNeedsExpansion(prev => {
        // Only update if the set actually changed
        const prevStr = Array.from(prev).sort().join(',');
        const newStr = Array.from(newNeedsExpansion).sort().join(',');
        if (prevStr !== newStr) {
          return newNeedsExpansion;
        }
        return prev;
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [remarksKey]);

  // Measure remarks container height after needsExpansion is determined and buttons are rendered
  // This ensures we measure after "Show more" buttons are in the DOM
  useEffect(() => {
    // Use requestAnimationFrame twice to ensure DOM is fully updated after state changes
    let rafId1: number;
    let rafId2: number;
    
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        if (remarksRef.current) {
          const height = remarksRef.current.offsetHeight;
          setRemarksHeight(prevHeight => {
            // Only update if height actually changed to avoid unnecessary re-renders
            if (prevHeight !== height) {
              return height;
            }
            return prevHeight;
          });
        }
      });
    });

    return () => {
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
    };
  }, [needsExpansion, expandedRemarks, hasRemarks]);

  return (
    <Container $bothCancelled={bothCancelled} $remarksHeight={hasRemarks ? remarksHeight : 0}>
      <TimeContainer time={leg.plannedDeparture} delaySec={leg.departureDelay} averageDelaySec={leg.depDelay?.averageDelay} cancelled={isDepartureCancelled} referenceDate={leg.plannedDeparture} />
      <Location name={leg.origin.name} platform={leg.departurePlatform} plannedPlatform={leg.plannedDeparturePlatform} cancelled={isDepartureCancelled} />
      <LineContainer>
        <BadgeContainer>
          <LineBadge lineName={getLineName(leg)} occupancy={leg.loadFactor} operatorId={leg.line?.operator?.id} operatorName={leg.line?.operator?.name} />
          <AmenitiesContainer>
            {filteredAmenities?.map((remark: { code?: string; }, index: number) => (
              <AmenityIcon key={`amenity-${index}-${remark.code}`} code={remark.code} />
            ))}
          </AmenitiesContainer>
        </BadgeContainer>
        <DestinationContainer $hasDirection={!!leg.direction}>
          <TypographyStyles.BodySRegular color={slateColors[800]}>
            {leg.direction ? `${t('journeyDetails.legCard.to')} ${leg.direction}` : ''}
          </TypographyStyles.BodySRegular>
          <TypographyStyles.BodySRegular color={slateColors[600]}>
            {formatDuration(getDurationInMinutes(leg.plannedDeparture, leg.plannedArrival))}
          </TypographyStyles.BodySRegular>
          {leg.stopovers && (
            <ClickableStopCount 
              color={slateColors[600]}
              $clickable={leg.stopovers.length > 2}
              onClick={() => leg.stopovers && leg.stopovers.length > 2 && setIsStopoversOpen(true)}
            >
              {leg.stopovers.length <= 2 ? t('journeyDetails.legCard.direct') : (() => {
                const totalStops = leg.stopovers.length - 1; // intermediate stops + 1
                return `${totalStops} ${totalStops === 1 ? t('journeyDetails.legCard.stop') : t('journeyDetails.legCard.stops')}`;
              })()}
            </ClickableStopCount>
          )}
        </DestinationContainer>
        {remark ? (
          <>
            <InfoContainer data-tooltip-id={`remark-${leg.id}-${leg.line.name}`}>
              <img alt="info" src={info} />
              <TypographyStyles.BodySRegular>{remark.summary}</TypographyStyles.BodySRegular>
            </InfoContainer>
            <StyledTooltip id={`remark-${leg.id}-${leg.line.name}`} place="top" content={remark.text} className="tooltip-styled">
            </StyledTooltip>
          </>
        ) : null}
      </LineContainer>
      {(leg.arrDelay || leg.depDelay) && <StatsContainer><Statistics leg={leg} /></StatsContainer>}
      <TimeContainer time={leg.plannedArrival} delaySec={leg.arrivalDelay} averageDelaySec={leg.arrDelay?.averageDelay} cancelled={isArrivalCancelled} referenceDate={leg.plannedDeparture} />
      <Location name={leg.destination.name} platform={leg.arrivalPlatform} plannedPlatform={leg.plannedArrivalPlatform} cancelled={isArrivalCancelled} />
      
      {/* Remarks section (exclude hints) */}
      {hasRemarks && (
        <RemarksContainer ref={remarksRef}>
          {filteredRemarks.map((remark: any, index: number) => {
            const isExpanded = expandedRemarks.has(index);
            const needsExpansionButton = needsExpansion.has(index);
            
            return (
              <RemarkItem key={`remark-${leg.id}-${index}`}>
                <RemarkContent>
                  <RemarkIcon size={14} />
                  <RemarkText 
                    ref={(el) => {
                      if (el) {
                        remarkTextRefs.current.set(index, el);
                      } else {
                        remarkTextRefs.current.delete(index);
                      }
                    }}
                    $expanded={isExpanded}
                  >
                    {remark.text}
                  </RemarkText>
                </RemarkContent>
                {needsExpansionButton && (
                  <ShowMoreButton
                    onClick={() => {
                      const newExpanded = new Set(expandedRemarks);
                      if (isExpanded) {
                        newExpanded.delete(index);
                      } else {
                        newExpanded.add(index);
                      }
                      setExpandedRemarks(newExpanded);
                    }}
                  >
                    {isExpanded ? (
                      <>
                        {t('journeyDetails.legCard.showLess')}
                        <ChevronUp size={12} />
                      </>
                    ) : (
                      <>
                        {t('journeyDetails.legCard.showMore')}
                        <ChevronDown size={12} />
                      </>
                    )}
                  </ShowMoreButton>
                )}
              </RemarkItem>
            );
          })}
        </RemarksContainer>
      )}
      
      {isStopoversOpen && leg.stopovers && (
        <Overlay onClick={() => setIsStopoversOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: '100%', display: 'flex', justifyContent: 'center' }}>
            <StopoversCard
              onClose={() => setIsStopoversOpen(false)}
              stopovers={leg.stopovers}
              originName={leg.origin.name}
              destinationName={leg.destination.name}
            />
          </div>
        </Overlay>
      )}
    </Container>
  )
}

export default JourneyLegCard;
